import { useNavigate } from 'react-router-dom';

import { z } from 'zod';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { useAuth } from '../../../../shared/hooks';
import {
  deserializer,
  ERROR_WEAK_PASSWORD,
  meetsMinPasswordScore,
} from '../../../../shared/utils';
import { WORKFLOW } from '../../../shared/constants';
import { signup as apiSignup } from '../services/registrationService';
import useAuthForm from './useAuthForm';

const DEFAULT_PLAN_ON_SIGNUP =
  import.meta.env.VITE_DEFAULT_PLAN_ON_SIGNUP || 'director_monthly';

const DEFAULT_VALUES = {
  businessName: '',
  email: '',
  firstName: '',
  lastName: '',
  password: '',
};

const formSchema = z
  .object({
    businessName: z.string().min(1, 'Business name is required.'),
    email: z.string().email('Invalid email address.'),
    firstName: z.string().min(1, 'First name is required.'),
    lastName: z.string().min(1, 'Last name is required.'),
    password: z.string().min(1, 'Password is required.'),
  })
  .refine(
    ({ businessName, email, firstName, lastName, password }) =>
      meetsMinPasswordScore({
        password,
        strengthInputs: [businessName, email, firstName, lastName],
      }),
    { message: ERROR_WEAK_PASSWORD, path: ['password'] }
  );

const useSignupForm = () => {
  const { startSession } = useAuth();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (formData, { setError }) => {
    const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardElement),
      billing_details: { email: formData.email },
    });

    if (stripeError) {
      setError({ code: stripeError.code || '', message: stripeError.message });
      return;
    }

    const response = await apiSignup({
      ...formData,
      paymentMethodId: paymentMethod.id,
      planId: DEFAULT_PLAN_ON_SIGNUP,
    });

    const jwtToken = response.headers.authorization;
    const user = await deserializer.deserialize(response.data);

    startSession({ jwtToken, entity: user });
    navigate(WORKFLOW);
  };

  return useAuthForm({
    defaultValues: DEFAULT_VALUES,
    onSubmit: handleSubmit,
    schema: formSchema,
  });
};

export default useSignupForm;
